import { EnableAccessGrupsStatus } from '~/components/containers/EnableAccessGroupsContainer/types';

interface LocalStorageFields {
  acceptedCookies?: string;
  mapTypeId?: string;
  token?: string;
  provider?: string;
  locale?: string;
  expiresAt?: number;
  refreshToken?: string;
}

const storeAuth = (attributes: LocalStorageFields) => {
  if (attributes.token) {
    localStorage.setItem('token', attributes.token);
  }

  if (attributes.provider) {
    localStorage.setItem('provider', attributes.provider);
  }

  if (attributes.locale) {
    localStorage.setItem('locale', attributes.locale);
  }

  if (attributes.expiresAt) {
    localStorage.setItem('expiresAt', attributes.expiresAt.toString());
  }
  if (attributes.refreshToken) {
    localStorage.setItem('refreshToken', attributes.refreshToken);
  }
};

const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('locale');
  localStorage.removeItem('provider');
  clearUser();
};

const getAuth = () => {
  return {
    token: localStorage.getItem('token'),
    provider: localStorage.getItem('provider'),
    expiresAt: localStorage.getItem('expiresAt'),
    refreshToken: localStorage.getItem('refreshToken'),
    locale: localStorage.getItem('locale'),
  };
};

const storeUser = (user: Record<string, any>) => {
  localStorage.setItem('user', JSON.stringify(user));
};
const clearUser = () => {
  localStorage.removeItem('user');
};
const getUser = (): Record<string, any> | undefined => {
  const userItem = localStorage.getItem('user');
  if (!userItem || userItem === '{}') {
    return undefined;
  }
  try {
    return JSON.parse(userItem);
  } catch (error) {
    console.error('Error parsing user from localStorage', error);
    return undefined;
  }
};

const getAcceptedCookies = () => {
  return localStorage.getItem('acceptedCookies');
};

const setAcceptedCookies = (): void => {
  localStorage.setItem('acceptedCookies', 'yes');
};

export const getMapTypeId = (): string | null => {
  return localStorage.getItem('mapTypeId') ?? 'satellite';
};

export const setMapTypeId = (mapTypeId: string): void => {
  localStorage.setItem('mapTypeId', mapTypeId);
};

export const getLastReadChangeLogItemDate = (defaultValue = ''): string => {
  return localStorage.getItem('lastReadChangeLogItemDate') || defaultValue;
};

export const setLastReadChangeLogItemDate = (date: string): void => {
  return localStorage.setItem('lastReadChangeLogItemDate', date);
};

export const getEnableUserAccessGroupsStatus = (
  companyId: string,
  defaultValue = EnableAccessGrupsStatus.NotRequested
): string => {
  return localStorage.getItem(`enableUserAccessGroupsStatus${companyId}`) || defaultValue;
};

export const setEnableUserAccessGroupsStatus = (
  companyId: string,
  status: EnableAccessGrupsStatus
): void => {
  return localStorage.setItem(`enableUserAccessGroupsStatus${companyId}`, status);
};

export interface UserCookiesConsent {
  acceptedCookies: boolean;
  showCookieBanner: boolean;
  consentDate: string | null;
}

const USER_COOKIES_CONSENT_KEY = 'userCookiesConsent';

export const getUserCookiesConsent = (): UserCookiesConsent | null => {
  try {
    const stored = localStorage.getItem(USER_COOKIES_CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const setUserCookiesConsent = (consent: UserCookiesConsent): void => {
  localStorage.setItem(USER_COOKIES_CONSENT_KEY, JSON.stringify(consent));
};

const INSTALL_BANNER_DISMISSED_AT_KEY = 'hfs.installBanner.dismissedAt';

export const getInstallBannerDismissedAt = (): number | null => {
  const raw = localStorage.getItem(INSTALL_BANNER_DISMISSED_AT_KEY);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const setInstallBannerDismissedAt = (timestamp: number): void => {
  localStorage.setItem(INSTALL_BANNER_DISMISSED_AT_KEY, String(timestamp));
};

const INCIDENT_BANNER_DISMISSED_KEY = 'hfs.incidentBanner.dismissed';

export const getIncidentBannerDismissed = (): boolean =>
  localStorage.getItem(INCIDENT_BANNER_DISMISSED_KEY) === 'true';

export const setIncidentBannerDismissed = (): void => {
  localStorage.setItem(INCIDENT_BANNER_DISMISSED_KEY, 'true');
};

export const clearIncidentBannerDismissed = (): void => {
  localStorage.removeItem(INCIDENT_BANNER_DISMISSED_KEY);
};

export const getOidcVerifier = () => {
  return localStorage.getItem('oidcVerifier');
};

export const setOidcVerifier = (value: string): void => {
  localStorage.setItem('oidcVerifier', value);
};

export const setUptimeDiamGroupId = (diamGroupId: string): void => {
  return localStorage.setItem('uptimeDiamGroupId', diamGroupId);
};

export const getUptimeDiamGroupId = (defaultValue = ''): string => {
  return localStorage.getItem('uptimeDiamGroupId') ?? defaultValue;
};

export const setUptimeDestination = (diamGroupId: string): void => {
  return localStorage.setItem('uptimeDestination', diamGroupId);
};

export const getUptimeDestination = (defaultValue = ''): string => {
  return localStorage.getItem('uptimeDestination') ?? defaultValue;
};

export const getUptimeNonceHash = () => {
  return localStorage.getItem('uptimeNonceHash');
};

export const setUptimeNonceHash = (value: string): void => {
  localStorage.setItem('uptimeNonceHash', value);
};

export const getUptimeStateHash = () => {
  return localStorage.getItem('uptimeStateHash');
};

export const setUptimeStateHash = (value: string): void => {
  localStorage.setItem('uptimeStateHash', value);
};

export default {
  storeAuth,
  clearAuth,
  getAuth,

  setAcceptedCookies,
  getAcceptedCookies,

  setMapTypeId,
  getMapTypeId,

  getEnableUserAccessGroupsStatus,
  setEnableUserAccessGroupsStatus,

  getUserCookiesConsent,
  setUserCookiesConsent,

  getOidcVerifier,
  setOidcVerifier,

  getUptimeNonceHash,
  setUptimeNonceHash,
  getUptimeStateHash,
  setUptimeStateHash,
  setUptimeDiamGroupId,
  getUptimeDiamGroupId,
  setUptimeDestination,
  getUptimeDestination,

  getUser,
  clearUser,
  storeUser,
};
